/** Photo lightbox (v1 photo-lightbox.js). Full-screen gallery overlay: arrows +
 * keyboard on desktop, swipe on mobile, a "3 / 12" counter, click-outside to
 * close. Controlled by the caller's open-index state (null = closed). Reused by
 * the lead drawer today and the deal/wholesale Photos tab (Parity 5/6). */
import { useCallback, useEffect, useRef, useState } from "react";
import { wrapIndex } from "./photoNav";

export function PhotoLightbox({
  photos,
  index,
  onClose,
}: {
  photos: string[];
  index: number;
  onClose: () => void;
}) {
  const [i, setI] = useState(index);
  const touch = useRef<{ x: number; y: number } | null>(null);
  const multi = photos.length > 1;

  const nav = useCallback(
    (d: number) => setI((prev) => wrapIndex(prev, d, photos.length)),
    [photos.length],
  );

  // keyboard nav; this Escape belongs to the lightbox, not the drawer beneath
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") nav(1);
      else if (e.key === "ArrowLeft") nav(-1);
      else if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, onClose]);

  if (!photos.length) return null;
  const cur = photos[i] ?? photos[0] ?? "";

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    const t = e.changedTouches[0];
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (multi && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) nav(dx < 0 ? 1 : -1);
    touch.current = null;
  };

  return (
    <div
      className="lightbox"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button className="lbbtn lbclose" onClick={onClose} aria-label="Close">×</button>
      {multi ? <div className="lbcount">{i + 1} / {photos.length}</div> : null}
      {multi ? (
        <>
          <button className="lbbtn lbnav lbprev" aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); nav(-1); }}>‹</button>
          <button className="lbbtn lbnav lbnext" aria-label="Next"
            onClick={(e) => { e.stopPropagation(); nav(1); }}>›</button>
        </>
      ) : null}
      <img className="lbimg" src={cur} draggable={false} alt="" />
    </div>
  );
}
